// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Pure utility functions for building location autocomplete options.
 *
 * This module handles the entire options pipeline:
 *   expand zones → partition into groups → sort within groups →
 *   inject zone parents → order groups → flatten
 *
 * No React, no hooks — just data transformation.
 */
import { SparseLocation, SparseLocationWithParent } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';

/**
 * Valid grouping methods for location search results.
 * Can be combined in an array to create hierarchical grouping.
 *
 * - 'inventory': Groups locations with isInventory=true first
 * - 'non-inventory': Groups locations with isInventory=false first (inverse of 'inventory')
 * - 'customer': Groups by customer name (or 'Manufacturer' if no customer)
 * - 'non-customer': Groups by presence/absence of customer
 * - 'none': No grouping applied
 *
 * Note: 'inventory' and 'non-inventory' are mutually exclusive.
 * Note: 'customer' and 'non-customer' are mutually exclusive.
 */
export type LocationGroupingMethod =
  | 'inventory'
  | 'non-inventory'
  | 'customer'
  | 'non-customer'
  | 'none';

/**
 * Configuration for a custom priority group that appears first in the location dropdown.
 * Locations matching the provided IDs will be moved to this group and removed from their
 * original groups. All non-priority locations will be displayed with italic styling.
 */
export interface CustomPriorityGroup {
  /**
   * The label to display for the priority group in the dropdown.
   */
  label: string;

  /**
   * Array of location IDs to include in the priority group.
   * IDs that don't match any available location are silently ignored.
   */
  ids: string[];
}

// ─── Zone option types ───────────────────────────────────────────────

/**
 * Internal metadata attached to SparseLocation options that represent zones.
 * These properties are stripped before passing values to consumers.
 */
interface ZoneOptionMetadata {
  _zoneName: string;
  _isDefaultZone: boolean;
  _isZoneParent: boolean;
  _parentName: string;
  _parentId: string;
}

type ZoneOption = SparseLocationWithParent & ZoneOptionMetadata;

export function isZoneOption(
  option: Pick<SparseLocation, 'id'>
): option is ZoneOption {
  return '_zoneName' in option;
}

/**
 * Removes internal zone metadata from a location option, returning a clean
 * SparseLocation suitable for passing to consumers.
 */
export function stripZoneMetadata(location: SparseLocation): SparseLocation {
  if (!isZoneOption(location)) {
    return location;
  }

  const {
    _zoneName,
    _isDefaultZone,
    _isZoneParent,
    _parentName,
    _parentId,
    ...clean
  } = location;
  return clean;
}

/**
 * Resolves the display zone name for a selected location value, or null when
 * the value is a plain site with no zone. Zone metadata is stripped once a
 * value is selected, so the zone is recovered from the fetched locations: a
 * named zone matches a zone entry by id, and a site that carries zones
 * resolves to its default zone.
 */
export function resolveSelectedZoneName(
  selected: Pick<SparseLocation, 'id'>,
  locations: SparseLocation[]
): string | null {
  for (const location of locations) {
    const zone = location.zones.find((entry) => entry.id === selected.id);
    if (zone) {
      return zone.name;
    }
  }

  const site = locations.find((location) => location.id === selected.id);
  if (site && site.zones.length > 0) {
    return 'Default';
  }

  return null;
}

/**
 * Suffix appended to parent location ID for the disabled zone parent option.
 * Prevents ID collision with the default zone (which keeps the parent's real ID).
 */
const ZONE_PARENT_ID_SUFFIX = ':zone-parent';

// ─── Step 1: Expand zones ────────────────────────────────────────────

/**
 * Expands sites that have zones into individual zone child options.
 *
 * - Sites WITHOUT zones pass through unchanged.
 * - Sites WITH zones are replaced by zone children only (no parent headers):
 *   - Default zone: clone of parent SparseLocation with zone metadata.
 *     Keeps parent's id/key/name so existing form values match.
 *   - Named zones: synthesized SparseLocation using zone's own id/key/name +
 *     fields inherited from parent. Uses zone's own isInventory, and names the
 *     site as its parentLocation, which a selected value keeps.
 *
 * Zone parent headers are NOT created here — they are injected later per-group
 * by injectZoneParents() so that each group gets its own header.
 */
export function expandLocationsWithZones(
  locations: SparseLocation[]
): SparseLocation[] {
  const result: SparseLocation[] = [];

  for (const location of locations) {
    if (location.zones.length === 0) {
      result.push(location);
      continue;
    }

    // Default zone (keeps parent's ID/isInventory so existing form values match)
    const defaultZone: ZoneOption = {
      ...location,
      _zoneName: 'Default',
      _isDefaultZone: true,
      _isZoneParent: false,
      parentLocation: null,
      _parentName: location.name,
      _parentId: location.id,
    };
    result.push(defaultZone);

    // Named zones — use the zone's own isInventory
    for (const zone of location.zones) {
      const namedZone: ZoneOption = {
        ...location,
        id: zone.id,
        key: zone.key,
        name: `${location.name} \u2014 ${zone.name}`,
        description: zone.description,
        consignee: zone.consignee,
        isInventory: zone.isInventory,
        isArchived: zone.isArchived,
        parentLocation: location,
        _zoneName: zone.name,
        _isDefaultZone: false,
        _isZoneParent: false,
        _parentName: location.name,
        _parentId: location.id,
      };
      result.push(namedZone);
    }
  }

  return result;
}

// ─── Step 2: Group label computation ─────────────────────────────────

/**
 * Computes the group label for a single option based on grouping methods.
 * Uses the option's own fields (especially isInventory), not derived from parent.
 */
export function getGroupLabel(
  option: SparseLocation,
  grouping: LocationGroupingMethod[] | undefined
): string {
  if (
    !grouping ||
    grouping.length === 0 ||
    grouping.every((m) => m === 'none')
  ) {
    return '';
  }

  const labels: string[] = [];

  for (const method of grouping) {
    switch (method) {
      case 'inventory':
      case 'non-inventory':
        labels.push(option.isInventory ? 'Inventory' : 'Non-inventory');
        break;
      case 'customer':
        labels.push(option.customer?.name ?? 'Manufacturer');
        break;
      case 'non-customer':
        labels.push(option.customer === null ? 'No customer' : 'Customer');
        break;
      case 'none':
        break;
    }
  }

  return labels.join(' / ');
}

// ─── Step 3: Partition into groups ───────────────────────────────────

interface PriorityConfig {
  customPriorityGroup?: CustomPriorityGroup;
  priorityCustomerId?: string;
}

/**
 * Partitions options into group buckets. Returns a Map<groupLabel, options[]>.
 *
 * When customPriorityGroup is set, matching options go into its label bucket.
 * Zone options match via _parentId.
 */
function partitionIntoGroups(
  options: SparseLocation[],
  grouping: LocationGroupingMethod[] | undefined,
  priority: PriorityConfig
): Map<string, SparseLocation[]> {
  const groups = new Map<string, SparseLocation[]>();

  for (const option of options) {
    let label: string;

    // Check custom priority group first
    if (priority.customPriorityGroup) {
      const checkId = isZoneOption(option) ? option._parentId : option.id;
      if (priority.customPriorityGroup.ids.includes(checkId)) {
        label = priority.customPriorityGroup.label;
      } else if (
        !grouping ||
        grouping.length === 0 ||
        grouping.every((m) => m === 'none')
      ) {
        label = 'Other locations';
      } else {
        label = getGroupLabel(option, grouping);
      }
    } else {
      label = getGroupLabel(option, grouping);
    }

    let bucket = groups.get(label);
    if (!bucket) {
      bucket = [];
      groups.set(label, bucket);
    }
    bucket.push(option);
  }

  return groups;
}

// ─── Step 4: Sort within group ───────────────────────────────────────

/**
 * Sorts options within a single group bucket.
 * - Non-transient before transient
 * - Non-archived before archived
 * - Alphabetical by name
 * - Zone families stay together: cross-family uses parent name,
 *   within-family: default first → named zones alphabetical.
 */
function sortWithinGroup(
  group: SparseLocation[],
  customSort?: (a: SparseLocation, b: SparseLocation) => number
): SparseLocation[] {
  return group.slice().sort((a, b) => {
    // Zone family clustering: options from the same parent stay together
    const aZone = isZoneOption(a);
    const bZone = isZoneOption(b);

    if (aZone && bZone && a._parentId === b._parentId) {
      // Within same family: default first, then alpha by zone name
      if (a._isDefaultZone !== b._isDefaultZone) {
        return a._isDefaultZone ? -1 : 1;
      }
      return a._zoneName.localeCompare(b._zoneName);
    }

    // Custom sort provided by consumer
    if (customSort) {
      return customSort(a, b);
    }

    // Cross-family: use parent name for zone options, own name for regular
    const aName = aZone ? a._parentName : a.name;
    const bName = bZone ? b._parentName : b.name;

    if (a.isTransient !== b.isTransient) {
      return a.isTransient ? 1 : -1;
    }

    if (a.isArchived !== b.isArchived) {
      return a.isArchived ? 1 : -1;
    }

    return aName.localeCompare(bName);
  });
}

// ─── Step 5: Inject zone parents ─────────────────────────────────────

/**
 * Creates a disabled zone parent header option for a given zone child.
 * The parent inherits the child's isInventory so it stays in the correct group.
 *
 * Known issue: when inventory grouping is combined with a zone family that has
 * mixed-inventory children (some inventory, some not), the parent header only
 * appears in one group (whichever child is encountered first). This is acceptable
 * because mixed-inventory zone families are uncommon in practice.
 */
function createZoneParentOption(
  child: ZoneOption,
  isInventory: boolean,
  uniqueKeySuffix: string
): ZoneOption {
  return {
    ...child,
    id: `${child._parentId}${ZONE_PARENT_ID_SUFFIX}${uniqueKeySuffix}`,
    name: child._parentName,
    isInventory,
    _zoneName: child._parentName,
    _isDefaultZone: false,
    _isZoneParent: true,
  };
}

/**
 * Scans a sorted group for zone children and inserts a disabled parent header
 * before each zone family. Because sorting already clusters zone families
 * together, we just need to detect when a new family starts.
 */
function injectZoneParents(
  group: SparseLocation[],
  groupLabel: string
): SparseLocation[] {
  const result: SparseLocation[] = [];
  const seenFamilies = new Set<string>();

  for (const option of group) {
    if (isZoneOption(option) && !option._isZoneParent) {
      if (!seenFamilies.has(option._parentId)) {
        seenFamilies.add(option._parentId);
        // Create a parent header. Use group label as part of the unique key suffix
        // to avoid ID collisions across groups.
        const parent = createZoneParentOption(
          option,
          option.isInventory,
          `:${groupLabel}`
        );
        result.push(parent);
      }
    }
    result.push(option);
  }

  return result;
}

// ─── Step 6: Order groups ────────────────────────────────────────────

/**
 * Returns group labels in display order based on grouping configuration.
 *
 * Priority: custom priority group first, then inventory order, then customer
 * order (priority customer → alpha → Manufacturer last), then alpha fallback.
 */
function orderGroups(
  groupMap: Map<string, SparseLocation[]>,
  config: BuildConfig
): string[] {
  const labels = Array.from(groupMap.keys());

  return labels.sort((a, b) => {
    // Custom priority group always first
    if (config.customPriorityGroup) {
      const aIsPriority = a === config.customPriorityGroup.label;
      const bIsPriority = b === config.customPriorityGroup.label;
      if (aIsPriority !== bIsPriority) {
        return aIsPriority ? -1 : 1;
      }
    }

    // Inventory / non-inventory ordering
    if (
      config.grouping?.includes('inventory') ||
      config.grouping?.includes('non-inventory')
    ) {
      const aIsNonInventory = a.includes('Non-inventory');
      const bIsNonInventory = b.includes('Non-inventory');

      if (aIsNonInventory !== bIsNonInventory) {
        const nonInventoryFirst = config.grouping.includes('non-inventory');
        return aIsNonInventory
          ? nonInventoryFirst
            ? -1
            : 1
          : nonInventoryFirst
            ? 1
            : -1;
      }
    }

    // Customer ordering: priority customer first, Manufacturer last, alpha between
    if (config.grouping?.includes('customer')) {
      // Priority customer first
      if (config.priorityCustomerId) {
        // We can't directly compare customer IDs from labels, but the label
        // contains the customer name. We use the group's first option instead.
        const aGroup = groupMap.get(a);
        const bGroup = groupMap.get(b);
        const aHasPriority = aGroup?.some(
          (opt) => opt.customer?.id === config.priorityCustomerId
        );
        const bHasPriority = bGroup?.some(
          (opt) => opt.customer?.id === config.priorityCustomerId
        );
        if (aHasPriority !== bHasPriority) {
          return aHasPriority ? -1 : 1;
        }
      }

      const aIsManufacturer = a.includes('Manufacturer');
      const bIsManufacturer = b.includes('Manufacturer');
      if (aIsManufacturer !== bIsManufacturer) {
        return aIsManufacturer ? 1 : -1;
      }
    }

    // Non-customer ordering: "No customer" first
    if (config.grouping?.includes('non-customer')) {
      const aIsNoCustomer = a.includes('No customer');
      const bIsNoCustomer = b.includes('No customer');
      if (aIsNoCustomer !== bIsNoCustomer) {
        return aIsNoCustomer ? -1 : 1;
      }
    }

    return a.localeCompare(b);
  });
}

// ─── Main pipeline ───────────────────────────────────────────────────

interface BuildConfig {
  grouping?: LocationGroupingMethod[];
  customPriorityGroup?: CustomPriorityGroup;
  priorityCustomerId?: string;
  sort?: (a: SparseLocation, b: SparseLocation) => number;
}

/**
 * Main pipeline: takes expanded options + grouping config, returns a flat
 * sorted array ready for MUI Autocomplete.
 *
 * Steps: partition → sort per group → inject zone parents → order groups → flatten
 */
export function buildGroupedOptions(
  options: SparseLocation[],
  config: BuildConfig
): SparseLocation[] {
  const hasGrouping =
    config.grouping &&
    config.grouping.length > 0 &&
    !config.grouping.every((m) => m === 'none');

  // If no grouping and no custom priority group, just sort and inject zone parents
  if (!hasGrouping && !config.customPriorityGroup) {
    const sorted = sortWithinGroup(options, config.sort);
    return injectZoneParents(sorted, '');
  }

  // Step 1: Partition into group buckets
  const groups = partitionIntoGroups(options, config.grouping, {
    customPriorityGroup: config.customPriorityGroup,
    priorityCustomerId: config.priorityCustomerId,
  });

  // Step 2: Sort within each group
  for (const [label, bucket] of groups) {
    groups.set(label, sortWithinGroup(bucket, config.sort));
  }

  // Step 3: Inject zone parents within each group
  for (const [label, bucket] of groups) {
    groups.set(label, injectZoneParents(bucket, label));
  }

  // Step 4: Order groups
  const orderedLabels = orderGroups(groups, config);

  // Step 5: Flatten
  const result: SparseLocation[] = [];
  for (const label of orderedLabels) {
    const bucket = groups.get(label);
    if (bucket) {
      result.push(...bucket);
    }
  }

  return result;
}

// ─── Filter utilities ────────────────────────────────────────────────

/**
 * Base filter function for location autocomplete. Includes zone name and
 * parent name in the searchable text for zone options.
 */
const muiFilter = createFilterOptions<SparseLocation>({
  stringify: (option) => {
    const base = `${option.name} ${option.address?.formattedAddress ?? ''}`;
    if (isZoneOption(option)) {
      if (option.isArchived) {
        return `${option._zoneName} ${option._parentName}`;
      }
      return `${base} ${option._zoneName} ${option._parentName}`;
    }
    return base;
  },
});

/**
 * Ensures zone parent options appear in filtered results whenever any of
 * their children are present. Inserts one parent before the first child of
 * each family. Uses _parentId to match families since zone parent IDs may
 * vary (e.g., when split across inventory groups).
 */
function ensureZoneParents(
  filtered: SparseLocation[],
  allOptions: SparseLocation[]
): SparseLocation[] {
  const parentIdsWithParent = new Set<string>();
  const parentIdsWithChildren = new Set<string>();

  for (const option of filtered) {
    if (isZoneOption(option)) {
      if (option._isZoneParent) {
        parentIdsWithParent.add(option._parentId);
      } else {
        parentIdsWithChildren.add(option._parentId);
      }
    }
  }

  const missingParentIds = new Set<string>();
  for (const parentId of parentIdsWithChildren) {
    if (!parentIdsWithParent.has(parentId)) {
      missingParentIds.add(parentId);
    }
  }

  if (missingParentIds.size === 0) {
    return filtered;
  }

  // Find the first zone parent for each missing family from the full options list
  const parentByFamilyId = new Map<string, SparseLocation>();
  for (const option of allOptions) {
    if (
      isZoneOption(option) &&
      option._isZoneParent &&
      missingParentIds.has(option._parentId) &&
      !parentByFamilyId.has(option._parentId)
    ) {
      parentByFamilyId.set(option._parentId, option);
    }
  }

  const result: SparseLocation[] = [];
  const inserted = new Set<string>();

  for (const option of filtered) {
    if (isZoneOption(option) && !option._isZoneParent) {
      if (
        missingParentIds.has(option._parentId) &&
        !inserted.has(option._parentId)
      ) {
        const parent = parentByFamilyId.get(option._parentId);
        if (parent) {
          result.push(parent);
        }
        inserted.add(option._parentId);
      }
    }
    result.push(option);
  }

  return result;
}

/**
 * Applies MUI filter then injects missing zone parents.
 */
export function locationFilter(
  options: SparseLocation[],
  state: FilterOptionsState<SparseLocation>
): SparseLocation[] {
  const filtered = muiFilter(options, state);
  return ensureZoneParents(filtered, options);
}
